import {ParamComponent} from 'panel';
import keyBy from 'lodash/keyBy';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2reactSnabbdomWrapper} from 'common/util/react-snabbdom-wrapper';
import {TIMEZONES} from 'common/constants';
import {ChartType} from 'common/report/constants';
import {getBookmarkParamsWithOverrides} from 'common/report/dashboards/bookmark-overrides';
import {FunnelStepBookmarkType} from 'common/report/funnels/charts/funnels-step-chart/types';
import {FunnelsAggregateType} from 'common/report/funnels/models/types';
import {RetentionBookmarkType} from 'common/report/retention/charts/types';

import {template} from './template';

export class Card extends ParamComponent<any, EmptyObj, IronContextRegistry> {
  get config() {
    return {
      template,
      defaultParams: {
        dataGroups: null,
        bookmark: null,
        chartParams: null,
        utcOffset: 0,
        projectId: ``,
      },
      params: {
        dataGroups: Object,
        cardTitle: String,
        chartOptions: Object,
        projectTimezone: String,
        reportLoading: Boolean,
        utcOffset: Number,
        bookmark: Object,
        chartParams: Object,
        projectId: String,
        chartEventsInfo: Object,
      },
      contexts: [
        `urlManager` as const,
        `currentProject` as const,
        `localStorageManager` as const,
        `currentUser` as const,
        `metrics` as const,
      ],
    };
  }

  connectedCallback() {
    super.connectedCallback();
  }

  ChartType = ChartType;

  getDataGroups = () => {
    // return Record<string, DataGroup>
    // this format is used in line and bar charts
    if (!this.params.dataGroups) {
      return null;
    } else {
      return keyBy(this.params.dataGroups, `groupID`);
    }
  };

  getBookmarkParamsWithDashboardOverrides = () => {
    // Common report chart widgets accept raw bookmark params
    const bookmark = this.params.bookmark;
    const chartOptions = this.params.chartOptions;
    const timeFilter = this.params.timeFilter;
    return getBookmarkParamsWithOverrides({
      bookmarkParams: bookmark.params,
      bookmarkType: bookmark.type,
      chartTypeOverride: chartOptions.type,
      timeFilterOverride: timeFilter,
      additionalFilters: this.params.globalFilters,
    });
  };

  getIsNotSupported = (): boolean => {
    return this.params.reportNotSupported;
  };

  isFunnelAggregateChart = (chartType) => {
    const bookmark = this.getBookmarkParamsWithDashboardOverrides();
    if (chartType === ChartType.FunnelFrequencyBar) {
      return bookmark.frequencyAggregateType === FunnelsAggregateType.Median;
    } else if (chartType === ChartType.FunnelTTCBar) {
      return bookmark.ttcAggregateType === FunnelsAggregateType.Median;
    }

    throw new Error(`Unsupported chart type for funnels aggregate determination`);
  };

  getFunnelTTCBarChartAttributes = () => ({
    'project-as-of-date': this.params.projectAsOfDate,
    'project-timezone': this.params.projectTimezone as typeof TIMEZONES[number],
  });

  getFunnelFrequencyBarChartAttributes = () => ({
    'project-id': String(this.params.projectId),
    'project-as-of-date': this.params.projectAsOfDate,
    'project-timezone': this.params.projectTimezone as typeof TIMEZONES[number],
  });

  getFunnelStepsChartWidgetProps = () => ({
    bookmarkData: {
      queryResults: this.params.chartParams,
      bookmark: this.getBookmarkParamsWithDashboardOverrides(),
      type: FunnelStepBookmarkType.FUNNELS,
    },
    projectDetails: {
      projectId: this.params.projectId.toString(),
      projectAsOfDate: this.params.projectAsOfDate,
      projectTimeZone: this.params.projectTimezone as typeof TIMEZONES[number],
      utcOffset: this.params.utcOffset,
      timePeriodSettings: null,
    },
    // TODO: public-mode context functionality
    // note: setting `null` should trigger the chart widget to fetch its own custom events using Stores
    customEvents: this.params.isPublicMode ? [] : null,
  });
  getFunnelCommonChartWidgetProps = () => ({
    funnelsResponse: this.params.chartParams,
    bookmark: this.getBookmarkParamsWithDashboardOverrides(),
    projectDetails: {
      projectId: this.params.projectId.toString(),
      projectAsOfDate: this.params.projectAsOfDate,
      projectTimeZone: this.params.projectTimezone as typeof TIMEZONES[number],
      utcOffset: this.params.utcOffset,
      timePeriodSettings: null,
    },
    // TODO: public-mode context functionality
    // note: setting `null` should trigger the chart widget to fetch its own custom events using Stores
    customEvents: this.params.isPublicMode ? [] : null,
  });

  getFlowSankeyChartAttributes = () => ({
    loaded: !this.params.reportLoading,
    'scroll-offset': 0,
    'tooltip-enabled': true,
    'disable-add-steps': true,
    'disable-click-node': true,
  });

  getFlowsChartWidgetProps = () => ({
    paramsAndResults: {
      params: this.getBookmarkParamsWithDashboardOverrides(),
      results: this.params.chartParams,
    },
  });

  getPropsForRetentionChart = () => {
    const params = this.getBookmarkParamsWithDashboardOverrides();
    return {
      paramsAndResults: {
        results: this.params.chartParams,
        params,
      },
    };
  };

  getAttributesForRetentionChart = () => {
    return {
      'project-timezone': this.params.projectTimezone,
      'utc-offset': this.params.utcOffset,
      'limit-displayed-segments': true,
    };
  };

  getAttributesForRetentionCurveChart = () => this.getAttributesForRetentionChart();
  getPropsForRetentionCurveChart = () => {
    const params = this.getBookmarkParamsWithDashboardOverrides();
    return {
      bookmarkData: {
        bookmark: params,
        queryResults: this.params.chartParams,
        type: RetentionBookmarkType.RETENTION,
      },
    };
  };
  getAttributesForRetentionTrendsChart = () => this.getAttributesForRetentionChart();
  getPropsForRetentionTrendsChart = () => this.getPropsForRetentionChart();

  getParamsForRetentionMetricChart = () => {
    const params = this.getBookmarkParamsWithDashboardOverrides();
    return {
      paramsAndResults: {
        results: this.params.chartParams,
        params,
      },
      projectTimezone: this.params.projectTimezone,
      limitDisplayedSegments: true,
      segmentDisplayLimit: 6,
    };
  };
}

export const CardEl = defineAndMakeJsxFactory<any>(`mp-dash-card-immersive`, Card);
export const CardSnabbdom = panel2reactSnabbdomWrapper<any>({
  jsxFactory: CardEl,
});
